import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { BackgroundMessage } from '../../../components/background-message';
import { PortfolioPerformanceContext } from '../../../providers/portfolio-performance';
import { PortfolioPerformanceHeader } from '../components/portfolio-performance-header';

import { PortfolioPerformanceAccountCard } from './portfolio-performance-cards/portfolio-performance-account-card';
import { BtcTickerBand } from '../components/market-ticker-band';
import { PortfolioPerformanceOverviewTable } from '../components/portfolio-performance-overview-table';
import { PerformanceComparisonCard } from '../components/cards/performance-comparison-card';
import { Box } from '@mui/material';
import { dilettaPanelSx } from '../../../components/v2/widgets/exotics-shared';
import { GlobalContext } from '../../../providers/global-provider';
import './styles.scss';
import { GlobalTabsKeysEnum } from '../../../providers/models/custom-app-links';
import { ReferenceStrategyToken } from '../../../providers/portfolio-performance/reference-strategies/use-reference-strategy-tokens';
import { useReferenceStrategyReports } from '../../../providers/portfolio-performance/reference-strategies/use-reference-strategy-reports';
import { isReferenceStrategyAccount } from '../../../providers/portfolio-performance/reference-strategies/reference-strategy.constants';
import { loadOpenAccountName, saveOpenAccountName } from '../selection-storage';

interface IProps {
  className: string;
  // Passed down from portfolio-performance/index.tsx rather than calling
  // useReferenceStrategyTokens() here too — two independent instances of
  // that hook raced (and could even collide on reqId, both being
  // Date.now()-keyed) such that one could time out empty while the other
  // succeeded, silently dropping reference-strategy reports for affected
  // clients even though their real account reports loaded fine. One fetch,
  // shared, fixes both failure modes.
  referenceTokens: ReferenceStrategyToken[];
}

const PortfolioPerformanceContent = ({ className, referenceTokens }: IProps) => {
  const { currentTab } = useContext(GlobalContext);
  const {
    selectedAccounts,
    reportsByAccount,
    loadingReportsByAccount,
    errorReportsByAccount,
    fetchReport,
    isNamesBlurred,
  } = useContext(PortfolioPerformanceContext);
  const [openAccountName, setOpenAccountName] = useState<string | null>(null);
  // Subset of rows the user has ticked to plot in the comparison chart.
  // Independent of both the sidebar multi-select and the open-card row —
  // defaults empty, so the chart starts as an explicit-select prompt.
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  // Hydrated from the last visit (localStorage) — only explicit user
  // actions (opening/closing a card) update the stored value, matching this
  // ref's existing distinction from automatic first-row defaulting below
  // (which only ever sets openAccountName state, never this ref).
  const accountNameCardOpenRef = useRef<string | null>(loadOpenAccountName());
  // Reference strategies now cover the full perps universe (~80+ tokens),
  // not a fixed 6 - fetching candle history unconditionally for all of them
  // would mean dozens of calls for tokens no client selected. Only fetches
  // for the reference accounts actually in selectedAccounts (a reference row
  // can still be toggled on/off at any time from the sidebar - its data
  // fetch just starts/stops with that selection now, instead of always
  // being ready in advance).
  const selectedReferenceTokens = useMemo(
    () => referenceTokens.filter((t) => selectedAccounts.some((a) => a.name === t.account.name)),
    [referenceTokens, selectedAccounts],
  );
  const referenceReportsByAccount = useReferenceStrategyReports(selectedReferenceTokens);

  // Merge real + reference reports once with a stable identity. The comparison
  // panel's chart overlay effect refits (destroying the user's pan/zoom)
  // whenever the series inputs change identity; a fresh object literal here
  // would do that on every unrelated render (ticking a comparison row,
  // websocket report updates for other accounts). Memoised, the merged object
  // only changes when the underlying reports actually change.
  const mergedReportsByAccount = useMemo(
    () => ({ ...reportsByAccount, ...referenceReportsByAccount }),
    [reportsByAccount, referenceReportsByAccount],
  );

  const classNameDefault = `portfolio-performance-content ${className}`;

  // Fetched here (for every selected REAL account) rather than left to each
  // account card's own mount effect, since only the open account's card
  // renders below now — the overview table still needs every account's
  // report to show its row, whether or not that account has ever been
  // opened. Reference strategies are skipped - they have no real backend
  // report, their data comes from useReferenceStrategyReports above.
  useEffect(() => {
    selectedAccounts.forEach((account) => {
      if (isReferenceStrategyAccount(account.name)) return;
      if (
        reportsByAccount[account.name] ||
        loadingReportsByAccount[account.name] ||
        errorReportsByAccount[account.name]
      ) {
        return;
      }
      fetchReport(account);
    });
  }, [
    selectedAccounts,
    reportsByAccount,
    loadingReportsByAccount,
    errorReportsByAccount,
    fetchReport,
  ]);

  // Re-opens whichever account the user had open if they navigate away and
  // back to the tab; otherwise defaults to the first row in the Overview
  // table's current (unsorted) order, so a first-time visitor lands
  // straight on a breakdown instead of an empty "select an account" state.
  useEffect(() => {
    if (currentTab !== GlobalTabsKeysEnum.portfolioPerformance) {
      setOpenAccountName(null);
      return;
    }

    if (selectedAccounts.length === 0) {
      accountNameCardOpenRef.current = null;
      setOpenAccountName(null);
      saveOpenAccountName(null);
      return;
    }

    const rememberedAccountName = accountNameCardOpenRef.current;
    const hasRememberedAccountSelected =
      rememberedAccountName != null &&
      selectedAccounts.some((account) => account.name === rememberedAccountName);

    setOpenAccountName(
      hasRememberedAccountSelected ? rememberedAccountName : selectedAccounts[0].name,
    );
  }, [currentTab, selectedAccounts]);

  // Opens (not toggles) an account — distinct from onToggle below, which a
  // card's own header uses to open/close itself. Selecting a different row
  // in the overview table should always land you on that account, not
  // close the one you're currently viewing.
  const openAccount = (accountName: string) => {
    accountNameCardOpenRef.current = accountName;
    setOpenAccountName(accountName);
    saveOpenAccountName(accountName);
  };

  const toggleComparison = (accountName: string) => {
    setSelectedForComparison((prev) =>
      prev.includes(accountName)
        ? prev.filter((name) => name !== accountName)
        : [...prev, accountName],
    );
  };

  // Only the ticked rows plot. Filtering selectedAccounts (rather than the
  // name array itself) keeps the card fed with live account objects and
  // drops any name whose row has since left the sidebar selection. Memoised
  // so the comparison panel's chart overlay keeps a stable `accounts`
  // identity across unrelated parent renders (e.g. websocket report updates)
  // — without this, `.filter` returns a fresh array each render and the
  // overlay refits, destroying the user's pan/zoom.
  const comparisonAccounts = useMemo(
    () => selectedAccounts.filter((account) => selectedForComparison.includes(account.name)),
    [selectedAccounts, selectedForComparison],
  );

  if (selectedAccounts.length === 0) {
    return (
      <div className={classNameDefault}>
        <BtcTickerBand />
        <BackgroundMessage
          classname="pp-background-message"
          message={{
            title: 'Select data for the calculation',
            description:
              'Choose the data you want to include in the risk calculation from the side menu and click on Calculate.',
          }}
        />
      </div>
    );
  }

  return (
    <div className={classNameDefault}>
      <PortfolioPerformanceHeader />
      <BtcTickerBand />
      {/* <PortfolioPerformanceTotalCard /> */}
      <PortfolioPerformanceOverviewTable
        accounts={selectedAccounts}
        reportsByAccount={mergedReportsByAccount}
        openAccountName={openAccountName}
        onSelectAccount={openAccount}
        selectedForComparison={selectedForComparison}
        onToggleComparison={toggleComparison}
      />
      <Box sx={{ ...dilettaPanelSx, p: 2.5, mb: 2 }}>
        <PerformanceComparisonCard
          accounts={comparisonAccounts}
          reportsByAccount={mergedReportsByAccount}
          isNamesBlurred={isNamesBlurred}
        />
      </Box>
      {(() => {
        const currentAccount = selectedAccounts.find((account) => account.name === openAccountName);

        if (!currentAccount) {
          return (
            <BackgroundMessage
              classname="pp-background-message"
              message={{
                title: 'Select an account',
                description: 'Click a row in the overview table above to open its card.',
              }}
            />
          );
        }

        const closeCard = () => {
          accountNameCardOpenRef.current = null;
          setOpenAccountName(null);
          saveOpenAccountName(null);
        };

        const isReference = isReferenceStrategyAccount(currentAccount.name);

        return (
          <PortfolioPerformanceAccountCard
            key={currentAccount.name}
            account={currentAccount}
            isOpen
            onToggle={closeCard}
            referenceReport={
              isReference ? (referenceReportsByAccount[currentAccount.name] ?? null) : undefined
            }
          />
        );
      })()}
    </div>
  );
};

export default PortfolioPerformanceContent;
